import { useEffect } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import Button from '@/components/ui/Button'
import { AppointmentLocation } from '@/components/ui/AppointmentLocation'
import { Card, CardContent } from '@/components/ui/Card'
import { CheckCircle, Calendar, Clock, User, MapPin } from 'lucide-react'
import { useCustomerAppointmentById } from '@/api/hooks/customer.hooks'
import { VisitType } from '@/api/enums'
import { formatAppointmentTime, formatLongDate } from '@/utils/date'

export default function Confirmation() {
  const navigate = useNavigate()
  const { appointmentId } = useSearch({ from: '/confirmation' })
  const { data: appointment, isLoading, error } = useCustomerAppointmentById(appointmentId ?? '')

  useEffect(() => {
    if (!appointmentId) navigate({ to: '/' })
  }, [appointmentId, navigate])

  if (error || (!isLoading && !appointment)) {
    return (
      <div className='min-h-screen bg-background flex items-center justify-center p-4'>
        <Card className='w-full max-w-md'>
          <CardContent className='p-6 text-center space-y-4'>
            <div className='mx-auto w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center'>
              <span className='text-2xl'>!</span>
            </div>
            <h2 className='text-xl font-semibold'>Appointment Not Found</h2>
            <p className='text-muted-foreground'>We couldn't find the appointment details. Please check your email for confirmation.</p>
            <Button onClick={() => navigate({ to: '/' })} className='w-full'>Back to Home</Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const skeleton = <span className='inline-block h-4 w-36 animate-pulse rounded bg-muted' />
  const appointmentTime = appointment?.startTime

  return (
    <div className='min-h-screen bg-background'>
      <main className='flex-1 flex items-center justify-center px-4 py-12'>
        <div className='w-full max-w-md space-y-8'>
          <div className='text-center'>
            <div className='mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4'>
              <CheckCircle className='w-8 h-8 text-green-600' />
            </div>
            <h1 className='headline text-3xl text-foreground mb-2'>Request Submitted!</h1>
            <p className='body-copy text-muted-foreground'>Your appointment request has been successfully submitted.</p>
          </div>

          <Card>
            <CardContent className='p-6 space-y-4'>
              <h2 className='text-lg font-semibold text-foreground mb-4'>What happens next?</h2>
              <div className='space-y-4'>
                <div className='flex items-start gap-3'>
                  <div className='w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center mt-0.5'><Clock className='w-3 h-3 text-primary' /></div>
                  <div>
                    <p className='text-sm font-medium text-foreground'>Review & Confirmation</p>
                    <p className='text-sm text-muted-foreground'>The specialist will review your request and confirm availability within 24 hours.</p>
                  </div>
                </div>
                <div className='flex items-start gap-3'>
                  <div className='w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center mt-0.5'><Calendar className='w-3 h-3 text-primary' /></div>
                  <div>
                    <p className='text-sm font-medium text-foreground'>Email Notification</p>
                    <p className='text-sm text-muted-foreground'>You'll receive an email confirmation with appointment details and next steps.</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className='p-6 space-y-3'>
              <h2 className='text-lg font-semibold text-foreground mb-4'>Appointment Summary</h2>
              <div className='flex items-center gap-3'>
                <div className='w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center'><User className='w-3 h-3 text-primary' /></div>
                {isLoading ? skeleton : <span className='text-sm text-foreground'>{appointment?.specialistName}</span>}
              </div>
              <div className='flex items-center gap-3'>
                <div className='w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center'><Calendar className='w-3 h-3 text-primary' /></div>
                {isLoading ? skeleton : <span className='text-sm text-foreground'>{appointmentTime && formatLongDate(appointmentTime)}</span>}
              </div>
              <div className='flex items-center gap-3'>
                <div className='w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center'><Clock className='w-3 h-3 text-primary' /></div>
                {isLoading ? skeleton : <span className='text-sm text-foreground'>{appointmentTime && formatAppointmentTime(appointmentTime)}</span>}
              </div>
              {(isLoading || appointment?.practiceName) && (
                <div className='flex items-center gap-3'>
                  <div className='w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center'><MapPin className='w-3 h-3 text-primary' /></div>
                  {isLoading ? skeleton : <span className='text-sm text-foreground'>{appointment?.practiceName}</span>}
                </div>
              )}
              {appointment && (
                <AppointmentLocation
                  address={appointment.locationAddress}
                  isTelehealth={appointment.visitType === VisitType.Telemedicine}
                />
              )}
            </CardContent>
          </Card>

          <div className='bg-accent/20 border border-accent/30 rounded-lg p-4'>
            <p className='text-sm text-foreground'><strong>Important:</strong> Please check your email (including spam folder) for updates on your appointment request.</p>
          </div>

          <div className='space-y-3'>
            <Button onClick={() => navigate({ to: '/' })} className='w-full'>Back to Home</Button>
            <div className='text-center'>
              <p className='text-sm text-muted-foreground mb-2'>Need to make changes or have questions?</p>
              <a href='mailto:contact@apptlyhealth.com' className='text-sm text-primary hover:text-primary/80 font-medium transition-colors'>Contact Support</a>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
